import { type ThreadId } from "@taskin-code/contracts";
import { create } from "zustand";

import { useComposerDraftStore } from "./composerDraftStore";

/**
 * Stop treating a thread as disposable — it now holds a message, so the
 * lifecycle in `useDisposableThreadLifecycle` must not delete it (and its
 * worktree) when the user navigates away.
 *
 * Disposability is tracked in two places, an ephemeral marker and the
 * persisted draft metadata, and a thread survives only if BOTH say so — hence
 * clearing both here rather than at either call site.
 */
export function promoteThreadFromTemporary(threadId: ThreadId): void {
  if (threadId.length === 0) return;
  useTemporaryThreadStore.getState().clearTemporaryThread(threadId);
  useComposerDraftStore.getState().setDraftThreadContext(threadId, { isTemporary: false });
}

interface TemporaryThreadStoreState {
  temporaryThreadIds: Record<ThreadId, true | undefined>;
  markTemporaryThread: (threadId: ThreadId) => void;
  clearTemporaryThread: (threadId: ThreadId) => void;
}

export const useTemporaryThreadStore = create<TemporaryThreadStoreState>((set) => ({
  temporaryThreadIds: {},
  markTemporaryThread: (threadId) => {
    if (threadId.length === 0) return;
    set((state) => {
      if (state.temporaryThreadIds[threadId]) {
        return state;
      }
      return {
        temporaryThreadIds: {
          ...state.temporaryThreadIds,
          [threadId]: true,
        },
      };
    });
  },
  clearTemporaryThread: (threadId) => {
    if (threadId.length === 0) return;
    set((state) => {
      if (!state.temporaryThreadIds[threadId]) {
        return state;
      }
      const nextTemporaryThreadIds = { ...state.temporaryThreadIds };
      delete nextTemporaryThreadIds[threadId];
      return { temporaryThreadIds: nextTemporaryThreadIds };
    });
  },
}));
